import { createSlice, PayloadAction } from '@reduxjs/toolkit'

// Discount slice state (equivalent to Flutter's DiscountController)
interface DiscountState {
  // Discount data
  hasDiscount: boolean
  discountCode: string
  discountAmount: number
  discountPercentage: number

  // Modal state
  isModalOpen: boolean
  isLoading: boolean

  // Form state
  codeController: string

  // Error handling
  error: string | null
}

const initialDiscountState: DiscountState = {
  // Discount data
  hasDiscount: false,
  discountCode: '',
  discountAmount: 0,
  discountPercentage: 0,

  // Modal state
  isModalOpen: false,
  isLoading: false,

  // Form state
  codeController: '',

  // Error handling
  error: null,
}

const discountSlice = createSlice({
  name: 'discount',
  initialState: initialDiscountState,
  reducers: {
    // Set discount data (equivalent to Flutter's setDiscountData)
    setDiscountData: (state, action: PayloadAction<{
      code: string
      amount: number
      percentage: number
    }>) => {
      state.hasDiscount = true
      state.discountCode = action.payload.code
      state.discountAmount = action.payload.amount
      state.discountPercentage = action.payload.percentage
    },

    // Clear discount (equivalent to Flutter's clearDiscount)
    clearDiscount: (state) => {
      state.hasDiscount = false
      state.discountCode = ''
      state.discountAmount = 0
      state.discountPercentage = 0
      state.codeController = '' // Clear the input field as well
    },

    // Modal controls
    openModal: (state) => {
      state.isModalOpen = true
      state.codeController = ''
      state.error = null
    },

    closeModal: (state) => {
      state.isModalOpen = false
      state.codeController = ''
      state.error = null
    },

    // Form controls
    updateCodeController: (state, action: PayloadAction<string>) => {
      state.codeController = action.payload
      state.error = null
    },

    // Loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },

    // Error handling
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
    },

    clearError: (state) => {
      state.error = null
    },
  },
})

export const {
  setDiscountData,
  clearDiscount,
  openModal,
  closeModal,
  updateCodeController,
  setLoading,
  setError,
  clearError,
} = discountSlice.actions

export default discountSlice.reducer
